import { Card, CardBody, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import { InstitutionPersonRole } from '../../common/Data';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import * as yup from 'yup';

import { Button } from '../../components';
import MultiSelect from 'react-multi-select-component';
import { ProgramStatus, States } from '../../common/Data';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';
import { ProfessionDto } from '../../common/Types';
import { professionService } from '../../services/ProfessionAdministration';
import { reportService } from '../../services/ReportService';
import moment from 'moment';

const StyledMultiSelect = styled(MultiSelect)`
    width: 300px;

    & .dropdown-container {
        width: 100%;
        max-width: 100%;
    }
    & .dropdown-content {
        z-index: 3;
        border: none !important;
        box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
        
        /* Remove borders from ALL nested elements */
        *, & > *, & * {
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
        }
        
        /* Force all child elements to have minimal left padding */
        & > * {
            padding-left: 0.25rem !important;
        }
        
        /* Position checkboxes properly to the left */
        input, [role="checkbox"], .checkbox, span, div {
            margin-left: -20px !important;
            margin-right: 0.5rem !important;
        }
    }
    & .select-item {
        display: flex !important;
        align-items: center !important;
        padding-left: 0.125rem !important;
        padding-right: 0.5rem !important;
        
        /* Ensure selection background spans full width */
        &.selected {
            background-color: rgba(13, 110, 253, 0.1) !important;
            width: 100% !important;
            margin-left: 0 !important;
            padding-left: 0.25rem !important;
        }
        
        /* Override the negative margins for selected items to fill width */
        &.selected input, &.selected [role="checkbox"], &.selected .checkbox, 
        &.selected span, &.selected div {
            margin-left: 0 !important;
        }
    }
`;
type Option = {
    label: string;
    value: string;
};

// Form data interface
interface InstitutionContactReportFormData {
    professionId?: number | null | undefined;
    selectedRole: Option[];
    selectedStatus: Option[];
    effectiveDate?: string | undefined;
    selectedStates: Option[];
}

// Validation schema
const validationSchema = yup.object({
    professionId: yup.number().nullable(),
    selectedRole: yup
        .array()
        .of(
            yup.object({
                label: yup.string().required(),
                value: yup.string().required(),
            }),
        )
        .default([]),
    selectedStatus: yup
        .array()
        .of(
            yup.object({
                label: yup.string().required(),
                value: yup.string().required(),
            }),
        )
        .default([]),
    effectiveDate: yup.string(),
    selectedStates: yup
        .array()
        .of(
            yup.object({
                label: yup.string().required(),
                value: yup.string().required(),
            }),
        )
        .default([]),
});

const InstitutionContactReport = () => {
    const [professions, setProfessions] = useState<ProfessionDto[]>([]);

    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm<InstitutionContactReportFormData>({
        resolver: yupResolver(validationSchema) as any,
        mode: 'onChange',
        defaultValues: {
            professionId: null,
            selectedRole: [],
            selectedStatus: [],
            effectiveDate: '',
            selectedStates: [],
        },
    });

    const watchedValues = watch();

    // Memoized profession options
    const professionOptions = useMemo(() => {
        return professions
            .filter((p) => p.professionId === 32 || !p.isDeleted)
            .sort((a, b) => a.title.localeCompare(b.title));
    }, [professions]);

    // All four ContactRole values must appear as selectable options. Using an explicit
    // list (instead of filtering Object.entries on a numeric enum with reverse-mapping)
    // avoids any ambiguity in iteration order and guarantees Billing Contact and
    // Program Director are present.
    const roleOptions = useMemo(
        () => [
            { label: 'CEO', value: String(InstitutionPersonRole.CEO) },
            { label: 'Dean', value: String(InstitutionPersonRole.Dean) },
            { label: 'Program Director', value: String(InstitutionPersonRole['Program Director']) },
            { label: 'Billing Contact', value: String(InstitutionPersonRole['Billing Contact']) },
        ],
        [],
    );

    const statusOptions = useMemo(
        () =>
            Object.entries(ProgramStatus)
                .filter((p) => !isNaN(p[0] as any))
                .map((p) => ({
                    label: String(p[1]),
                    value: String(p[0]),
                })),
        [],
    );

    const stateOptions = useMemo(
        () =>
            States?.map((s) => ({
                label: String(s.name),
                value: String(s.abbreviation),
            })) || [],
        [],
    );

    useEffect(() => {
        const loadProfessions = async () => {
            try {
                const results = await professionService.getProfessions();
                setProfessions(results);
            } catch {
                toast.error('Error loading professions');
            }
        };
        loadProfessions();
    }, []);

    const exportReport = useCallback(() => {
        const toastId = toast.info('Generating report, this may take a minute...', {
            autoClose: false,
        });

        reportService
            .exportInstitutionContactReport(
                watchedValues.professionId,
                watchedValues.selectedRole?.map((s) => Number(s.value)),
                watchedValues.selectedStatus?.map((s) => Number(s.value)),
                watchedValues.selectedStates?.map((s) => s.value),
                watchedValues.effectiveDate ? moment(watchedValues.effectiveDate).format() : undefined,
            )
            .then((report) => {
                if (report) {
                    const url = window.URL.createObjectURL(report);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `InstitutionContactReport.xlsx`;
                    a.click();
                    toast.update(toastId, {
                        render: 'Report generated successfully.',
                        type: 'success',
                        autoClose: 2500,
                        closeOnClick: true,
                        draggable: true,
                    });
                }
            })
            .catch(() => {
                toast.update(toastId, {
                    render: 'Unable to generate report',
                    type: 'error',
                    autoClose: 5000,
                    closeOnClick: true,
                    draggable: true,
                });
            });
    }, [
        watchedValues.professionId,
        watchedValues.selectedRole,
        watchedValues.selectedStatus,
        watchedValues.selectedStates,
        watchedValues.effectiveDate,
    ]);

    return (
        <>
            <Card>
                <CardBody>
                    <Row>
                        <Col>
                            <div className="mb-3">
                                <h3>Institution Contact Report</h3>
                            </div>
                            <Form className="d-flex align-items-center flex-wrap" style={{ gap: '1rem' }}>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="selectedRole" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Role
                                    </Label>
                                    <Controller
                                        name="selectedRole"
                                        control={control}
                                        render={({ field }) => (
                                            <StyledMultiSelect
                                                options={roleOptions}
                                                disableSearch={true}
                                                hasSelectAll={false}
                                                value={field.value || []}
                                                onChange={field.onChange}
                                                labelledBy="Role"
                                            />
                                        )}
                                    />
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="professionId" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Profession
                                    </Label>
                                    <Controller
                                        name="professionId"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="select"
                                                {...field}
                                                value={field.value || ''}
                                                onChange={(e) =>
                                                    field.onChange(e.target.value ? Number(e.target.value) : null)
                                                }
                                                style={{ width: '200px' }}
                                            >
                                                <option value="">Select...</option>
                                                {professionOptions.map((p) => (
                                                    <option key={p.professionId} value={p.professionId}>
                                                        {p.title}
                                                    </option>
                                                ))}
                                            </Input>
                                        )}
                                    />
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="selectedStatus" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Status
                                    </Label>
                                    <Controller
                                        name="selectedStatus"
                                        control={control}
                                        render={({ field }) => (
                                            <StyledMultiSelect
                                                options={statusOptions}
                                                disableSearch={true}
                                                hasSelectAll={false}
                                                value={field.value || []}
                                                onChange={field.onChange}
                                                labelledBy="Status"
                                            />
                                        )}
                                    />
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="effectiveDate" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        Effective Date (Exact):
                                    </Label>
                                    <Controller
                                        name="effectiveDate"
                                        control={control}
                                        render={({ field }) => (
                                            <Input
                                                type="date"
                                                {...field}
                                                value={field.value || ''}
                                                onChange={(e) => field.onChange(e.target.value)}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '0.5rem' }}>
                                    <Label for="selectedStates" className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                                        State
                                    </Label>
                                    <Controller
                                        name="selectedStates"
                                        control={control}
                                        render={({ field }) => (
                                            <StyledMultiSelect
                                                options={stateOptions}
                                                disableSearch={true}
                                                hasSelectAll={false}
                                                value={field.value || []}
                                                onChange={field.onChange}
                                                labelledBy="State"
                                            />
                                        )}
                                    />
                                </div>
                            </Form>
                        </Col>
                    </Row>
                    <div className="d-flex justify-content-end mt-2">
                        <Button type="button" color="info" onClick={() => exportReport()}>
                            Export Report
                        </Button>
                    </div>
                </CardBody>
            </Card>
        </>
    );
};

export default InstitutionContactReport;
